import React, { Fragment, useState, useRef, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FaTimes, FaUser, FaLock, FaSave, FaEye, FaEyeSlash } from 'react-icons/fa';

// Define the component as a normal function (not using memo for now)
const PlatformForm = ({ 
  isOpen, 
  onClose, 
  platform, 
  formData, 
  formErrors, 
  handleInputChange, 
  handleSavePlatform, 
  loading 
}) => {
  const isEditing = !!platform;
  const [showPassword, setShowPassword] = useState(false);
  const [isModifyingPassword, setIsModifyingPassword] = useState(false);
  
  // Use refs to minimize state changes during typing
  const nameRef = useRef(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);

  // When in edit mode, use the platform's password directly
  useEffect(() => {
    if (isEditing && platform && platform.password) {
      // Update the password field with the platform's password
      // This simulates an onChange event to update formData
      handleInputChange({
        target: {
          name: 'password',
          value: platform.password
        }
      });
    }
  }, [isEditing, platform, handleInputChange]);
  
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={onClose}
        initialFocus={nameRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95 translate-y-2"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-2"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-0 text-left align-middle shadow-xl transition-all border border-gray-200">
                <div 
                  className="px-6 py-4"
                  style={{
                    background: 'linear-gradient(to right, #059669, #0d9488)'
                  }}
                >
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-white flex justify-between items-center"
                  >
                    <span className="flex items-center">
                      <svg 
                        className="w-5 h-5 mr-2"
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15 9H9V15H15V9Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {isEditing ? 'Modifier la plateforme' : 'Ajouter une plateforme'}
                    </span>
                    <button
                      type="button"
                      className="text-white/80 hover:text-white focus:outline-none transition-colors"
                      onClick={onClose}
                      aria-label="Fermer"
                    >
                      <FaTimes className="h-5 w-5" />
                    </button>
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-white/80">
                    {isEditing 
                      ? 'Modifier les détails de cette plateforme de commande' 
                      : 'Ajouter une nouvelle plateforme pour gérer les commandes'}
                  </p>
                </div>
                
                <div className="p-6 space-y-5">
                  {/* Platform name input */}
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 flex items-center">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 w-1 h-4 rounded mr-2"></span>
                      Nom de la plateforme
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FaUser className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        ref={nameRef}
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Entrez le nom de la plateforme"
                        className={`pl-10 block w-full rounded-lg border ${
                          formErrors.name 
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
                        } transition-colors sm:text-sm py-2.5`}
                        aria-invalid={formErrors.name ? "true" : "false"}
                        aria-describedby={formErrors.name ? "name-error" : undefined}
                      />
                      {formErrors.name && (
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-red-500 rounded-r-lg"></div>
                      )}
                    </div>
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-start" id="name-error">
                        <svg className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  
                  {/* Username input */}
                  <div className="space-y-1.5">
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 flex items-center">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 w-1 h-4 rounded mr-2"></span>
                      Nom d'utilisateur
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FaUser className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        ref={usernameRef}
                        type="text"
                        name="username"
                        id="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        placeholder="Entrez le nom d'utilisateur"
                        className={`pl-10 block w-full rounded-lg border ${
                          formErrors.username 
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
                        } transition-colors sm:text-sm py-2.5`}
                        aria-invalid={formErrors.username ? "true" : "false"}
                        aria-describedby={formErrors.username ? "username-error" : undefined}
                      />
                      {formErrors.username && (
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-red-500 rounded-r-lg"></div>
                      )}
                    </div>
                    {formErrors.username && (
                      <p className="mt-1 text-sm text-red-600 flex items-start" id="username-error">
                        <svg className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {formErrors.username}
                      </p>
                    )}
                  </div>
                  
                  {/* Type Platform dropdown */}
                  <div className="space-y-1.5">
                    <label htmlFor="typePlateform" className="block text-sm font-medium text-gray-700 flex items-center">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 w-1 h-4 rounded mr-2"></span>
                      Type de plateforme
                    </label>
                    <div>
                      <select
                        id="typePlateform"
                        name="typePlateform"
                        value={formData.typePlateform || ''}
                        onChange={handleInputChange}
                        className={`block w-full rounded-lg border ${
                          formErrors.typePlateform ? 'border-red-300' : 'border-gray-300'
                        } py-2.5 px-3 sm:text-sm`}
                      >
                        <option value="">Sélectionnez un type</option>
                        <option value="Insta">Insta</option>
                        <option value="Mylerz">Mylerz</option>
                        <option value="jax">Jax</option>
                         <option value="fiablo">Fiablo</option>
                         <option value="navex">Navex</option>
                        <option value="adex">Adex</option>
                      </select>
                    </div>
                    {formErrors.typePlateform && (
                      <p className="mt-1 text-sm text-red-600" id="typePlateform-error">{formErrors.typePlateform}</p>
                    )}
                  </div>
                  
                  {/* Password input */}
                  <div className="space-y-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 flex items-center">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 w-1 h-4 rounded mr-2"></span>
                      Mot de passe
                    </label>
                    
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FaLock className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        ref={passwordRef}
                        type={showPassword ? "text" : "password"}
                        name="password"
                        id="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Minimum 6 caractères"
                        className={`pl-10 pr-10 block w-full rounded-lg border ${
                          formErrors.password 
                            ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
                        } transition-colors sm:text-sm py-2.5`}
                        aria-invalid={formErrors.password ? "true" : "false"}
                        aria-describedby={formErrors.password ? "password-error" : undefined}
                      />
                      <button
                        type="button"
                        tabIndex="-1"
                        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      >
                        {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                      </button>
                      {formErrors.password && (
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-red-500 rounded-r-lg"></div>
                      )}
                    </div>
                    
                    {formErrors.password && (
                      <p className="mt-1 text-sm text-red-600 flex items-start" id="password-error">
                        <svg className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {formErrors.password}
                      </p>
                    )}
                    {isEditing && (
                      <p className="mt-1 text-xs text-gray-500 flex items-start">
                        <svg className="h-3.5 w-3.5 mr-1 mt-0.5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Vous pouvez modifier le mot de passe actuel de la plateforme.
                      </p>
                    )}
                  </div>

                  {/* Frais Retour input */}
                  <div className="space-y-1.5">
                    <label htmlFor="fraisRetour" className="block text-sm font-medium text-gray-700 flex items-center">
                      <span className="bg-gradient-to-r from-emerald-500 to-teal-500 w-1 h-4 rounded mr-2"></span>
                      Frais de retour (TND)
                    </label>
                    <div className="relative group">
                      <input
                        type="number"
                        name="fraisRetour"
                        id="fraisRetour"
                        min="0"
                        step="0.01"
                        value={formData.fraisRetour}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        className="block w-full rounded-lg border border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 transition-colors sm:text-sm py-2.5 px-3"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Footer with action buttons */}
                <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3 rounded-b-2xl border-t border-gray-100">
                  <button
                    type="button"
                    className="inline-flex justify-center items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center items-center rounded-lg border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      loading 
                        ? 'bg-emerald-500 opacity-80 cursor-not-allowed' 
                        : 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
                    }`}
                    onClick={handleSavePlatform}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Traitement...
                      </>
                    ) : (
                      <>
                        <FaSave className="h-4 w-4 mr-1.5" />
                        {isEditing ? 'Mettre à jour' : 'Créer'}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Export the component as default
export default PlatformForm;